/**
 * Navigation abstraction to support gradual React Router migration.
 *
 * This allows components to navigate regardless of whether they're rendered
 * within a React Router context or not.
 *
 * Usage:
 * - Wrap component tree with NavigationProvider (pass navigate if Router available)
 * - Child components use useAppNavigation() to navigate
 *
 * When migration is complete, delete this file and use useNavigate() directly.
 */

import {useContext, createContext} from 'react'
import PropTypes from 'prop-types'
import {useNavigate, useLocation} from 'react-router'

const NavigationContext = createContext(null)

/**
 * Provider component that wraps the component tree to provide navigation.
 *
 * @param {function|null} navigate - Navigation function from React Router's useNavigate()
 *                                    Pass null if React Router context is not available
 */
const NavigationProviderComponent = ({children, navigate}) => (
  <NavigationContext.Provider value={navigate}>
    {children}
  </NavigationContext.Provider>
)

NavigationProviderComponent.propTypes = {
  children: PropTypes.node.isRequired,
  navigate: PropTypes.func,
}

NavigationProviderComponent.defaultProps = {
  navigate: null,
}

export const NavigationProvider = NavigationProviderComponent

/**
 * Hook for child components to access navigation.
 * Works whether React Router is available or not.
 *
 * @returns {function} Navigation function that accepts a path string
 */
export const useAppNavigation = () => {
  const navigate = useContext(NavigationContext)

  return (
    navigate ||
    ((path) => {
      window.location.href = path
    })
  )
}

/**
 * Hook for boundary components (NavigationProvider wrappers) to conditionally
 * get navigate from React Router if available.
 *
 * Used at component boundaries to determine if Router context exists.
 *
 * @returns {function|null} useNavigate() if Router available, null otherwise
 */
export const useNavigateIfAvailable = () => {
  // Note: This violates rules of hooks by calling hooks conditionally,
  // but it's safe because the component using this hook is always either
  // within a Router context or never within one - it's consistent per mount location.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  try {
    useLocation() // Will throw if no Router context
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNavigate()
  } catch {
    return null
  }
}
